// Statistics tab component — redesigned per-tab stats view

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAniList } from '../context/AniListContext';
import { useTimeSettings } from '../context/TimeSettingsContext';
import {
    calculateTabStats,
    formatDuration,
    formatEpisodeProgress
} from '../utils/timeFormatter';
import { OP_ED_SKIP_MINUTES } from '../config/constants';
import { filterGroupsByStatus } from '../services/groupingService';
import { StatsSkeleton } from './common/SkeletonLoader';
import ErrorMessage from './common/ErrorMessage';
import './MyStats.css';

// Tab configuration matching the Overview/Dashboard tabs
const TAB_CONFIG = [
    { id: 'ALL', label: 'All', emoji: '📋', colorVar: '--stats-color-all' },
    { id: 'CURRENT', label: 'Watching', emoji: '🎬', colorVar: '--stats-color-watching' },
    { id: 'CONTINUE', label: 'Continue', emoji: '▶️', colorVar: '--stats-color-continue' },
    { id: 'PLANNING', label: 'Planning', emoji: '📝', colorVar: '--stats-color-planning' },
    { id: 'PAUSED', label: 'On Hold', emoji: '⏸️', colorVar: '--stats-color-onhold' },
    { id: 'DROPPED', label: 'Dropped', emoji: '💔', colorVar: '--stats-color-dropped' },
    { id: 'HALF_COMPLETED', label: 'Half Completed', emoji: '½', colorVar: '--stats-color-halfcompleted' },
    { id: 'COMPLETED', label: 'Completed', emoji: '✅', colorVar: '--stats-color-completed' }
];

const MyStats = () => {
    const { userData, isLoading, isRefreshing, error, refreshData, animeGroups } = useAniList();
    const { hoursPerDay, setHoursPerDay } = useTimeSettings();
    const [activeInfo, setActiveInfo] = useState(null);

    const toggleInfo = (e, id) => {
        e.stopPropagation();
        setActiveInfo(prev => prev === id ? null : id);
    };

    // Click outside to dismiss
    useEffect(() => {
        if (!activeInfo) return;
        const handleClickOutside = () => setActiveInfo(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [activeInfo]);

    // Calculate per-tab stats from group-level data
    const tabStats = useMemo(() => {
        if (!animeGroups || animeGroups.length === 0) return null;

        const result = {};
        TAB_CONFIG.forEach(tab => {
            result[tab.id] = calculateTabStats(animeGroups, tab.id, filterGroupsByStatus);
        });
        return result;
    }, [animeGroups]);

    const handleRefresh = () => {
        refreshData(true);
    };

    if (!userData) {
        return (
            <div className="statistics-container">
                <div className="empty-state">
                    <div className="empty-icon">📊</div>
                    <h2>No Statistics Yet</h2>
                    <p>Connect your AniList account in Settings to see your stats!</p>
                </div>
            </div>
        );
    }

    if (isLoading && !tabStats) {
        return <StatsSkeleton />;
    }

    if (error && !tabStats) {
        return <ErrorMessage message={error} onRetry={handleRefresh} />;
    }

    // Get the "ALL" tab stats for the overall summary
    const allStats = tabStats?.['ALL'] || {};

    // "Animes Watched" = groups from all tabs except Planning
    const animesWatchedCount = (tabStats?.['CURRENT']?.groupCount || 0)
        + (tabStats?.['CONTINUE']?.groupCount || 0)
        + (tabStats?.['PAUSED']?.groupCount || 0)
        + (tabStats?.['DROPPED']?.groupCount || 0)
        + (tabStats?.['HALF_COMPLETED']?.groupCount || 0)
        + (tabStats?.['COMPLETED']?.groupCount || 0);

    // "Different Animes Planned" = groups from Planning tab only
    const animesPlannedCount = tabStats?.['PLANNING']?.groupCount || 0;

    return (
        <div className="statistics-container">
            {/* Header */}
            <div className="stats-header">
                <h1 className="stats-title">📊 My Stats</h1>
                <div className="stats-header-controls">
                    <div className="stats-hours-config">
                        <label htmlFor="hours-per-day">1 day =</label>
                        <input
                            id="hours-per-day"
                            type="number"
                            min="1"
                            max="24"
                            value={hoursPerDay}
                            onChange={(e) => setHoursPerDay(e.target.value)}
                            className="hours-input"
                        />
                        <span className="hours-label">hours</span>
                    </div>
                </div>
            </div>

            {/* OP/ED Skip Explanation */}
            <div className="oped-info-note">
                <span className="oped-info-icon">ℹ️</span>
                <span>All times exclude an average <strong>{OP_ED_SKIP_MINUTES} min</strong> per episode for OP/ED skips.</span>
            </div>

            {/* Overall Summary */}
            <div className="stats-overall-summary">

                <div className="summary-card-new total-anime">
                    <div className="summary-icon">🎌</div>
                    <div className="summary-data">
                        <div className="summary-value-new">
                            {allStats.animeCount || 0}
                            <span className="info-wrapper">
                                <button className={`info-btn ${activeInfo === 'totalAnime' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'totalAnime')}>i</button>
                                {activeInfo === 'totalAnime' && <span className="info-popover">Number of anime you have in AniList</span>}
                            </span>
                        </div>
                        <div className="summary-label-new">Total Anime</div>
                    </div>
                </div>
                <div className="summary-card-new total-groups">
                    <div className="summary-icon">📚</div>
                    <div className="summary-data">
                        <div className="summary-value-new">
                            {allStats.groupCount || 0}
                            <span className="info-wrapper">
                                <button className={`info-btn ${activeInfo === 'totalGroups' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'totalGroups')}>i</button>
                                {activeInfo === 'totalGroups' && <span className="info-popover">Number of unique animes</span>}
                            </span>
                        </div>
                        <div className="summary-label-new">Total Groups</div>
                    </div>
                </div>
                <div className="summary-card-new total-episodes">
                    <div className="summary-icon">🎬</div>
                    <div className="summary-data">
                        <div className="summary-value-new">
                            {formatEpisodeProgress(allStats.watchedEpisodes, allStats.totalEpisodes)}
                            <span className="info-wrapper">
                                <button className={`info-btn ${activeInfo === 'totalEpisodes' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'totalEpisodes')}>i</button>
                                {activeInfo === 'totalEpisodes' && <span className="info-popover">Episodes watched out of total episodes across your entire anime collection</span>}
                            </span>
                        </div>
                        <div className="summary-label-new">Episodes</div>
                    </div>
                </div>
                {animesWatchedCount > 0 && (
                    <div className="summary-card-new series-watched">
                        <div className="summary-icon">👁️</div>
                        <div className="summary-data">
                            <div className="summary-value-new">
                                {animesWatchedCount}
                                <span className="info-wrapper">
                                    <button className={`info-btn ${activeInfo === 'animesWatched' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'animesWatched')}>i</button>
                                    {activeInfo === 'animesWatched' && <span className="info-popover">Number of unique anime watched</span>}
                                </span>
                            </div>
                            <div className="summary-label-new">Different Animes Watched</div>
                        </div>
                    </div>
                )}
                {animesPlannedCount > 0 && (
                    <div className="summary-card-new animes-planned">
                        <div className="summary-icon">📝</div>
                        <div className="summary-data">
                            <div className="summary-value-new">
                                {animesPlannedCount}
                                <span className="info-wrapper">
                                    <button className={`info-btn ${activeInfo === 'animesPlanned' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'animesPlanned')}>i</button>
                                    {activeInfo === 'animesPlanned' && <span className="info-popover">Number of unique anime you plan to watch</span>}
                                </span>
                            </div>
                            <div className="summary-label-new">New Animes Planned</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Per-Tab Stats */}
            <div className="stats-tabs-section">
                <h2 className="section-title-new">📈 Stats by Category</h2>
                <div className="stats-tab-list">
                    {TAB_CONFIG.map(tab => {
                        const s = tabStats?.[tab.id];
                        if (!s) return null;

                        // Skip tabs with 0 groups (except ALL which always shows)
                        if (tab.id !== 'ALL' && s.groupCount === 0) return null;

                        return (
                            <div
                                key={tab.id}
                                className={`stats-tab-row tab-${tab.id.toLowerCase()}`}
                                style={{ '--tab-accent': `var(${tab.colorVar})` }}
                            >
                                {/* Tab Identity */}
                                <div className="tab-identity">
                                    <span className="tab-emoji">{tab.emoji}</span>
                                    <div className="tab-name-group">
                                        <span className="tab-name">{tab.label}</span>
                                        <span className="tab-meta">
                                            {s.groupCount} group{s.groupCount !== 1 ? 's' : ''} · {s.animeCount} anime
                                        </span>
                                    </div>
                                </div>

                                {/* Three Time Columns — Equal Prominence */}
                                <div className="tab-time-grid">
                                    <div className="time-cell total">
                                        <span className="time-cell-label">📺 Total</span>
                                        <span className="time-cell-value">{formatDuration(s.totalDuration, hoursPerDay)}</span>
                                    </div>
                                    <div className="time-cell watched">
                                        <span className="time-cell-label">✅ Watched</span>
                                        <span className="time-cell-value">{formatDuration(s.watchedDuration, hoursPerDay)}</span>
                                    </div>
                                    <div className="time-cell remaining">
                                        <span className="time-cell-label">⏳ Remaining</span>
                                        <span className="time-cell-value">{formatDuration(s.remainingDuration, hoursPerDay)}</span>
                                    </div>
                                </div>

                                {/* Episode & Score */}
                                <div className="tab-extras">
                                    <span className="extras-item">
                                        🎬 {formatEpisodeProgress(s.watchedEpisodes, s.totalEpisodes)} eps
                                    </span>
                                    {s.averageScore > 0 && (
                                        <span className="extras-item">
                                            ⭐ {(s.averageScore / 10).toFixed(1)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MyStats;
